import { useTranslation } from 'react-i18next';

type Lang = 'en' | 'tr';

/**
 * Compact EN/TR switch for the dashboard shell. Persists via i18next / LanguageDetector
 * (localStorage key i18nextLng) — no /tr URL prefix on app routes.
 */
export default function DashboardLanguageSwitch() {
  const { i18n, t } = useTranslation();
  const current: Lang = i18n.resolvedLanguage?.startsWith('tr') ? 'tr' : 'en';

  const setLang = (lang: Lang) => {
    if (current === lang) return;
    void i18n.changeLanguage(lang);
    try {
      localStorage.setItem('i18nextLng', lang);
    } catch {
      /* ignore storage errors */
    }
  };

  const btn = (lang: Lang, label: string) => (
    <button
      type="button"
      onClick={() => setLang(lang)}
      className={`min-w-[2.25rem] rounded-md px-2 py-1 text-xs font-semibold uppercase tracking-wide transition-colors ${
        current === lang
          ? 'bg-[var(--tenant-primary)] text-white shadow-sm'
          : 'text-[var(--tenant-text-muted)] hover:bg-[var(--tenant-nav-hover)] hover:text-[var(--tenant-title)]'
      }`}
      aria-pressed={current === lang}
      aria-label={label}
    >
      {lang === 'en' ? 'EN' : 'TR'}
    </button>
  );

  return (
    <div
      className="inline-flex items-center gap-0.5 rounded-lg border border-[var(--tenant-border)] bg-[var(--tenant-bg-subtle)] p-0.5"
      role="group"
      aria-label={t('layout.language')}
    >
      {btn('en', t('layout.langEn'))}
      {btn('tr', t('layout.langTr'))}
    </div>
  );
}
